import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedCastEntry } from 'fym-shared/src/music24/directedVideoApi';
import type { DirectedCastLibraryEntry } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { footageMessages } from '../../messages/footageMessages';
import { ReferencePicker } from './ReferencePicker';
import { AI_CLIP_MAX_REFERENCES, referenceEntryOf } from './referenceDefaults';
/* eslint-disable css-modules/no-unused-class -- the editor stylesheet; this part uses its own slice of it */
import editorStyles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './ReferencePicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AiReferencesProps {
  /** True in a cut that a directed video made (design i1): the count shows "2 of 3". */
  isDirected: boolean;
  fileIds: number[];
  onChange: (fileIds: number[]) => void;
  library: DirectedCastLibraryEntry[];
  directedCast: DirectedCastEntry[];
  disabled: boolean;
}

/**
 * The References row of the AI clip form (directed video design i1, i3): up to 3 picked pictures with
 * a remove button, and "+" that opens the picker (i2). The form shows it only with a cast library.
 */
const AiReferencesComponent: React.FC<AiReferencesProps> = ({
  isDirected,
  fileIds,
  onChange,
  library,
  directedCast,
  disabled,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : wrapRef.current?.contains(event.target as Node) !== true
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [isOpen]);

  const isFull = fileIds.length >= AI_CLIP_MAX_REFERENCES;
  const hint = isDirected ? footageMessages.referencesHintDirected : footageMessages.referencesHintOther;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.head}>
        <span className={editorStyles.eyebrow}>{intl.formatMessage(footageMessages.references)}</span>
        <span className={styles.count}>
          {fileIds.length === 0 && !isDirected
            ? intl.formatMessage(footageMessages.referencesOptional)
            : intl.formatMessage(footageMessages.referencesCount, {
                count: fileIds.length,
                max: AI_CLIP_MAX_REFERENCES,
              })}
        </span>
      </div>
      <div className={styles.refs}>
        {fileIds.map((fileId) => {
          const entry = referenceEntryOf(fileId, library, directedCast);
          const label = entry?.label ?? '';
          return (
            <div key={fileId} className={clsx(styles.ref, styles.refFull)}>
              {entry !== null && entry.url !== '' && <img src={entry.url} alt="" />}
              <button
                type="button"
                className={styles.refRemove}
                aria-label={intl.formatMessage(footageMessages.referencesRemove, { label })}
                disabled={disabled}
                onClick={() => onChange(fileIds.filter((id) => id !== fileId))}
              >
                ×
              </button>
              <span className={styles.refLabel}>{label}</span>
            </div>
          );
        })}
        {!isFull && (
          <button
            type="button"
            className={styles.ref}
            aria-label={intl.formatMessage(footageMessages.referencesAdd)}
            aria-expanded={isOpen}
            disabled={disabled}
            onClick={() => setIsOpen((open) => !open)}
          >
            +
          </button>
        )}
        <span className={clsx(editorStyles.hint, fileIds.length === 0 ? styles.refsHint : styles.refsHintFull)}>
          {intl.formatMessage(hint)}
        </span>
      </div>
      {isOpen && (
        <ReferencePicker
          library={library}
          selected={fileIds}
          onDone={(picks) => {
            onChange(picks);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
};

export const AiReferences = TypedMemo(AiReferencesComponent);
